import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Box, Chip, CircularProgress, Tab, Tabs, Typography } from '@mui/material';
import { AnimatePresence, LayoutGroup, motion, useReducedMotion } from 'framer-motion';
import { CheckCircleOutline, Inbox } from '@mui/icons-material';
import { WarehouseAPI } from '../services/api';
import { ActionableItem, ActionableRuleMeta } from '../types';
import { useAuth } from '../contexts/AuthContext';
import ActionableCard from '../components/actionables/ActionableCard';
import TeamView from '../components/actionables/TeamView';
import RulesView from '../components/actionables/RulesView';
import TablePaginationBar, { DEFAULT_PAGE_SIZE } from '../components/common/TablePaginationBar';
import { useUrlParam } from '../components/common/useUrlParam';
import { formatIstDate } from '../components/actionables/actionableUtils';
import { display, eyebrow } from '../components/actionables/brand';

const OPEN_PAGE_SIZE = DEFAULT_PAGE_SIZE;
const EASE = [0.16, 1, 0.3, 1] as const;

// A rule in the left rail. The orange indicator slides between rows (shared layoutId) so the
// current filter is always obvious, and counts update in place.
const RailItem: React.FC<{ label: string; count?: number; active: boolean; onClick: () => void; reduceMotion: boolean | null }> = ({ label, count, active, onClick, reduceMotion }) => (
    <Box
        component="button" type="button" onClick={onClick} aria-pressed={active}
        sx={{
            position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, width: '100%',
            px: 1.5, minHeight: 40, border: 0, bgcolor: 'transparent', borderRadius: '10px', cursor: 'pointer', textAlign: 'left',
            font: 'inherit', fontSize: '0.84rem', fontWeight: active ? 800 : 600, color: active ? '#1A140F' : 'rgba(26,20,15,0.65)',
            transition: 'color 150ms', '&:hover': { color: '#1A140F', bgcolor: active ? 'transparent' : 'rgba(26,20,15,0.04)' }, '&:focus-visible': { outline: '2px solid #DC5829', outlineOffset: 1 },
        }}
    >
        {active && (
            <motion.span
                layoutId="actionables-rail"
                transition={reduceMotion ? { duration: 0 } : { type: 'spring', stiffness: 500, damping: 38 }}
                style={{ position: 'absolute', inset: 0, borderRadius: 10, background: 'rgba(220, 88, 41, 0.1)', boxShadow: 'inset 3px 0 0 #DC5829' }}
            />
        )}
        <span style={{ position: 'relative', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
        {count !== undefined && (
            <Box component="span" sx={{ position: 'relative', minWidth: 24, px: 0.75, height: 20, borderRadius: 999, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem', fontWeight: 800, bgcolor: active ? '#DC5829' : 'rgba(26,20,15,0.07)', color: active ? '#fff' : 'inherit', fontVariantNumeric: 'tabular-nums' }}>
                {count}
            </Box>
        )}
    </Box>
);

const ActionablesPage: React.FC = () => {
    const { user } = useAuth();
    const reduceMotion = useReducedMotion();
    // Actionables scoping: the Team/Rules tabs are admin-only. Post-RBAC the admin role key
    // is 'admin' (the backend ADMIN_ROLES rename + migration mapped the old 'super-admin'
    // here), so recognise admins — same visibility as before, just the renamed role.
    const isSuperAdmin = !!user?.isAdmin || user?.role === 'admin';
    // Where the user is lives in the URL, so a refresh (or a shared link) lands in the same place.
    const [tabParam, setTabParam] = useUrlParam('tab', 'mine');
    const [statusParam, setStatusParam] = useUrlParam('s', 'open');
    const [ruleFilter, setRuleParam] = useUrlParam('r', '');
    const [pageParam, setPageParam] = useUrlParam('p', '0');
    const tab: 'mine' | 'team' | 'rules' = isSuperAdmin && (tabParam === 'team' || tabParam === 'rules') ? tabParam : 'mine';
    const status: 'open' | 'closed' = statusParam === 'closed' ? 'closed' : 'open';
    const page = Math.max(0, parseInt(pageParam, 10) || 0);
    const setTab = (v: 'mine' | 'team' | 'rules') => setTabParam(v);
    const setStatus = (v: 'open' | 'closed') => setStatusParam(v, ['p']);
    const setRuleFilter = (v: string | ((f: string) => string)) => setRuleParam(typeof v === 'function' ? v(ruleFilter) : v, ['p']);
    const setPage = (n: number) => setPageParam(String(n));
    const [rules, setRules] = useState<ActionableRuleMeta[]>([]);
    const [items, setItems] = useState<ActionableItem[]>([]);
    const [openByRule, setOpenByRule] = useState<Record<string, number>>({});
    const [doneToday, setDoneToday] = useState(0);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const ruleTitles = useMemo(() => Object.fromEntries(rules.map(r => [r.key, r.title])), [rules]);
    const openTotal = useMemo(() => Object.values(openByRule).reduce((a, b) => a + b, 0), [openByRule]);
    const dayTotal = openTotal + doneToday;

    const load = useCallback(async () => {
        setLoading(true); setError(null);
        try {
            const res = await WarehouseAPI.getMyActionables({ status, rule: ruleFilter || undefined, page: page + 1, limit: status === 'open' ? 200 : 25 });
            setItems(res.items); setTotal(res.total); setOpenByRule(res.openByRule); setDoneToday(res.doneToday);
        } catch (e: any) { setError(e?.response?.data?.message || 'Could not load your actionables.'); }
        finally { setLoading(false); }
    }, [status, ruleFilter, page]);

    useEffect(() => { WarehouseAPI.getActionableRules().then(setRules).catch(() => undefined); }, []);
    useEffect(() => { load(); }, [load]);

    // Full rule-ordered grouping of every open item currently loaded (the fetch ceiling is 200, a
    // safe cap for a user's own open set). The on-screen page is sliced out of this below.
    const grouped = useMemo(() => {
        const m = new Map<string, ActionableItem[]>();
        items.forEach(i => { if (!m.has(i.ruleKey)) m.set(i.ruleKey, []); m.get(i.ruleKey)!.push(i); });
        const order = rules.map(r => r.key);
        const pos = (k: string) => (order.indexOf(k) === -1 ? 99 : order.indexOf(k));
        return Array.from(m.entries()).sort((a, b) => pos(a[0]) - pos(b[0]));
    }, [items, rules]);

    // The ordered open items for the current rail selection, and the paged slice regrouped under
    // their rule headings so the group headers + descriptions still render.
    const openTotalForView = useMemo(() => grouped.reduce((a, [, list]) => a + list.length, 0), [grouped]);
    const pagedGroups = useMemo(() => {
        const flat = grouped.flatMap(([ruleKey, list]) => list.map(it => [ruleKey, it] as const));
        const slice = flat.slice(page * OPEN_PAGE_SIZE, page * OPEN_PAGE_SIZE + OPEN_PAGE_SIZE);
        const m = new Map<string, ActionableItem[]>();
        slice.forEach(([ruleKey, it]) => { if (!m.has(ruleKey)) m.set(ruleKey, []); m.get(ruleKey)!.push(it); });
        return Array.from(m.entries());
    }, [grouped, page]);

    const handleDone = (item: ActionableItem) => {
        const nextOpen = Math.max(0, openTotalForView - 1);
        const lastPage = Math.max(0, Math.ceil(nextOpen / OPEN_PAGE_SIZE) - 1);
        if (page > lastPage) setPage(lastPage);
        setItems(prev => prev.filter(i => i._id !== item._id));
        setOpenByRule(prev => ({ ...prev, [item.ruleKey]: Math.max(0, (prev[item.ruleKey] || 1) - 1) }));
        setDoneToday(n => n + 1);
        window.dispatchEvent(new Event('actionables-changed'));
    };

    const ruleRail = (
        <Box component="nav" aria-label="Filter by rule" sx={{ display: 'flex', flexDirection: { xs: 'row', lg: 'column' }, flexWrap: { xs: 'wrap', lg: 'nowrap' }, gap: 0.25 }}>
            <RailItem label="All actionables" count={status === 'open' ? openTotal : undefined} active={!ruleFilter} onClick={() => setRuleFilter('')} reduceMotion={reduceMotion} />
            {rules.map(r => (
                <RailItem key={r.key} label={r.title} count={status === 'open' ? (openByRule[r.key] || 0) : undefined} active={ruleFilter === r.key} onClick={() => setRuleFilter(f => (f === r.key ? '' : r.key))} reduceMotion={reduceMotion} />
            ))}
        </Box>
    );

    return (
        <Box sx={{ width: '100%', px: { xs: 2, md: 3, xl: 4 }, py: 2.5 }}>
            {/* Hero */}
            <motion.div initial={reduceMotion ? false : { opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, ease: EASE }}>
                {tab === 'mine' ? (
                    // Option C — inbox-style header: white card with a left orange accent bar, live
                    // count chip, count-driven subline (no date/time) and a thin progress meter.
                    <Box
                        sx={{
                            position: 'relative', display: 'flex', alignItems: { xs: 'flex-start', sm: 'center' }, justifyContent: 'space-between',
                            gap: { xs: 2, sm: 3 }, flexWrap: 'wrap', mb: 2.5, pl: { xs: 2.5, md: 3.25 }, pr: { xs: 2.5, md: 3 }, py: { xs: 2, md: 2.25 },
                            borderRadius: '16px', border: '1px solid #ECECE4', bgcolor: '#fff', overflow: 'hidden',
                            boxShadow: '0 1px 2px rgba(26,20,15,0.04), 0 10px 28px rgba(26,20,15,0.05)',
                        }}
                    >
                        {/* Left orange accent bar */}
                        <Box sx={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 4, bgcolor: '#DC5829' }} />
                        <Box sx={{ minWidth: 0, flex: '1 1 300px' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                <Typography component="span" sx={{ ...display({ xs: '1.7rem', md: '2.1rem' }, '-0.035em') }}>Your actionables</Typography>
                                {openTotal > 0 ? (
                                    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', height: 24, px: 1.25, borderRadius: 999, fontSize: '0.78rem', fontWeight: 800, bgcolor: 'rgba(220,88,41,0.12)', color: '#B8451E', fontVariantNumeric: 'tabular-nums' }}>
                                        {openTotal} open
                                    </Box>
                                ) : (
                                    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, height: 24, px: 1.25, borderRadius: 999, fontSize: '0.78rem', fontWeight: 800, bgcolor: 'rgba(34,197,94,0.14)', color: '#15803d' }}>
                                        <CheckCircleOutline sx={{ fontSize: 16 }} /> All clear
                                    </Box>
                                )}
                            </Box>
                            <Typography sx={{ fontSize: '0.92rem', color: 'text.secondary', mt: 0.5 }}>
                                {openTotal === 0 ? 'Nothing is waiting on you. Nice.' : `${openTotal} thing${openTotal === 1 ? '' : 's'} need${openTotal === 1 ? 's' : ''} you${doneToday ? ` · ${doneToday} done today` : ''}`}
                            </Typography>
                            {dayTotal > 0 && (
                                <Box sx={{ mt: 1.5, maxWidth: 360 }}>
                                    <Box sx={{ position: 'relative', height: 6, borderRadius: 999, bgcolor: 'rgba(26,20,15,0.08)', overflow: 'hidden' }}>
                                        <Box
                                            sx={{
                                                position: 'absolute', left: 0, top: 0, bottom: 0, borderRadius: 999, bgcolor: '#16a34a',
                                                width: `${Math.round((doneToday / dayTotal) * 100)}%`,
                                                transition: reduceMotion ? 'none' : 'width 450ms cubic-bezier(0.16,1,0.3,1)',
                                            }}
                                        />
                                    </Box>
                                    <Typography sx={{ ...eyebrow, color: '#8A8078', mt: 0.75 }}>{Math.round((doneToday / dayTotal) * 100)}% done</Typography>
                                </Box>
                            )}
                        </Box>
                        {isSuperAdmin && (
                            <Tabs
                                value={tab} onChange={(_, v) => setTab(v)}
                                sx={{
                                    minHeight: 36, flexShrink: 0,
                                    '& .MuiTabs-indicator': { height: 2, borderRadius: 2, backgroundColor: '#DC5829' },
                                    '& .MuiTab-root': { textTransform: 'none', fontWeight: 700, minHeight: 36, px: 1.5, color: 'rgba(26,20,15,0.55)', '&.Mui-selected': { color: '#1A140F' } },
                                }}
                            >
                                <Tab value="mine" label="Mine" />
                                <Tab value="team" label="Team" />
                                <Tab value="rules" label="Rules" />
                            </Tabs>
                        )}
                    </Box>
                ) : (
                    <Box
                        sx={{
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 3, flexWrap: 'wrap', mb: 2.5, px: { xs: 2.5, md: 4 }, py: { xs: 2, md: 2.25 },
                            borderRadius: '20px', border: '1px solid #ece4d4', overflow: 'hidden', position: 'relative',
                            background: 'linear-gradient(120deg, #fff 0%, #fff7f0 55%, #ffeadb 100%)',
                            boxShadow: '0 1px 2px rgba(26,20,15,0.04), 0 14px 36px rgba(220,88,41,0.08)',
                        }}
                    >
                        <Box sx={{ position: 'absolute', right: -60, top: -80, width: 260, height: 260, borderRadius: '50%', background: 'radial-gradient(circle, rgba(220,88,41,0.16), transparent 70%)', pointerEvents: 'none' }} />
                        <Box sx={{ position: 'relative' }}>
                            <Typography sx={{ ...eyebrow, color: '#B8451E' }}>{tab === 'rules' ? 'Super-admin settings' : 'Team overview'}</Typography>
                            <Typography sx={{ ...display({ xs: '1.7rem', md: '2.3rem' }, '-0.035em'), mt: 0.75 }}>
                                {tab === 'rules' ? 'Actionable rules' : 'Actionables across the team'}
                            </Typography>
                            <Typography sx={{ fontSize: '0.92rem', color: 'text.secondary', mt: 0.5 }}>
                                {tab === 'rules' ? 'Switch rules on or off, tune them and choose who each one goes to.' : 'Every item, for everyone — open, closed and past.'}
                            </Typography>
                        </Box>
                        <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
                            {isSuperAdmin && (
                                <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ minHeight: 40, bgcolor: 'rgba(255,255,255,0.7)', borderRadius: '999px', px: 0.5, '& .MuiTabs-indicator': { display: 'none' }, '& .MuiTab-root': { textTransform: 'none', fontWeight: 700, minHeight: 36, borderRadius: '999px', mt: 0.5, mb: 0.5, '&.Mui-selected': { color: '#fff', bgcolor: '#DC5829' } } }}>
                                    <Tab value="mine" label="My actionables" />
                                    <Tab value="team" label="Team (all)" />
                                    <Tab value="rules" label="Rules" />
                                </Tabs>
                            )}
                        </Box>
                    </Box>
                )}
            </motion.div>

            {tab === 'rules' && isSuperAdmin ? <RulesView /> : tab === 'team' && isSuperAdmin ? <TeamView ruleTitles={ruleTitles} /> : (
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '248px minmax(0, 1fr)' }, gap: 3, alignItems: 'start' }}>
                    {/* Rail */}
                    <Box sx={{ position: { lg: 'sticky' }, top: { lg: 16 } }}>
                        <Box role="tablist" aria-label="Show" sx={{ display: 'flex', p: '3px', mb: 2, borderRadius: '999px', bgcolor: 'rgba(26,20,15,0.06)' }}>
                            {(['open', 'closed'] as const).map(s => (
                                <Box key={s} component="button" type="button" role="tab" aria-selected={status === s} onClick={() => setStatus(s)}
                                    sx={{ position: 'relative', flex: 1, border: 0, bgcolor: 'transparent', height: 32, borderRadius: '999px', font: 'inherit', fontSize: '0.8rem', fontWeight: 700, cursor: 'pointer', color: status === s ? '#1A140F' : 'rgba(26,20,15,0.55)', '&:focus-visible': { outline: '2px solid #DC5829' } }}>
                                    {status === s && <motion.span layoutId="act-status-pill" transition={reduceMotion ? { duration: 0 } : { type: 'spring', stiffness: 520, damping: 38 }} style={{ position: 'absolute', inset: 0, borderRadius: 999, background: '#fff', boxShadow: '0 1px 3px rgba(26,20,15,0.18)' }} />}
                                    <span style={{ position: 'relative' }}>{s === 'open' ? 'To do' : 'Closed'}</span>
                                </Box>
                            ))}
                        </Box>
                        <Typography sx={{ ...eyebrow, color: '#8A8078', mb: 0.75, px: 1 }}>Rules</Typography>
                        <LayoutGroup id="rail">{ruleRail}</LayoutGroup>
                    </Box>

                    {/* Content */}
                    <Box sx={{ minWidth: 0 }}>
                        {error && <Alert severity="error" sx={{ mb: 2, borderRadius: '10px' }}>{error}</Alert>}
                        {loading && items.length === 0 ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}><CircularProgress /></Box>
                        ) : status === 'open' ? (
                            grouped.length === 0 ? (
                                <motion.div initial={reduceMotion ? false : { opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.4, ease: EASE }}>
                                    <Box sx={{ textAlign: 'center', py: 10, borderRadius: '20px', bgcolor: '#fff', border: '1px dashed #d9d3c7' }}>
                                        <motion.div initial={reduceMotion ? false : { scale: 0.4, rotate: -20 }} animate={{ scale: 1, rotate: 0 }} transition={{ type: 'spring', stiffness: 260, damping: 14, delay: 0.1 }} style={{ display: 'inline-flex' }}>
                                            <CheckCircleOutline sx={{ fontSize: 56, color: '#16a34a' }} />
                                        </motion.div>
                                        <Typography sx={{ ...display('1.5rem'), mt: 1 }}>You're all clear</Typography>
                                        <Typography sx={{ fontSize: '0.9rem', color: 'text.secondary' }}>New actionables appear every morning around 9:30.</Typography>
                                    </Box>
                                </motion.div>
                            ) : (
                                <>
                                    {pagedGroups.map(([ruleKey, list]) => {
                                        const meta = rules.find(r => r.key === ruleKey);
                                        return (
                                            <Box key={ruleKey} sx={{ mb: 4 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.25, mb: 1.5, flexWrap: 'wrap' }}>
                                                    <Typography sx={{ ...display('1.35rem') }}>{meta?.title || ruleKey}</Typography>
                                                    <Chip size="small" label={list.length} sx={{ height: 22, fontWeight: 800, bgcolor: 'rgba(220,88,41,0.12)', color: '#B8451E' }} />
                                                    <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>{meta?.description}</Typography>
                                                </Box>
                                                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(auto-fill, minmax(560px, 1fr))' }, gap: 2 }}>
                                                    <AnimatePresence initial>
                                                        {list.map((it, i) => <ActionableCard key={it._id} item={it} index={i} onDone={handleDone} />)}
                                                    </AnimatePresence>
                                                </Box>
                                            </Box>
                                        );
                                    })}
                                    <TablePaginationBar page={page} total={openTotalForView} rowsPerPage={OPEN_PAGE_SIZE} onPageChange={setPage} />
                                </>
                            )
                        ) : (
                            <>
                                {items.length === 0 ? (
                                    <Box sx={{ textAlign: 'center', py: 8, color: 'text.secondary' }}><Inbox sx={{ fontSize: 40, opacity: 0.4 }} /><Typography sx={{ mt: 1 }}>Nothing closed yet.</Typography></Box>
                                ) : (
                                    <AnimatePresence initial>
                                        {items.map((it, i) => (
                                            <motion.div key={it._id} initial={reduceMotion ? false : { opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.22, delay: Math.min(i, 10) * 0.025, ease: EASE }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', px: 2.5, py: 1.5, mb: 1, borderRadius: '14px', bgcolor: '#fff', border: '1px solid #ececE4' }}>
                                                    <Box sx={{ flex: '1 1 320px', minWidth: 0 }}>
                                                        <Typography sx={{ fontWeight: 700, fontSize: '0.92rem' }}>{it.title}</Typography>
                                                        <Typography sx={{ fontSize: '0.74rem', color: 'text.secondary' }}>{ruleTitles[it.ruleKey] || it.ruleKey} · <span style={{ fontFamily: 'monospace' }}>{it.subjectKey}</span></Typography>
                                                    </Box>
                                                    <Chip size="small" label={it.resolution === 'verified' ? 'Done · verified' : it.resolution === 'auto' ? 'Resolved automatically' : 'Dismissed'} sx={{ height: 24, fontWeight: 700, fontSize: '0.7rem', bgcolor: it.resolution === 'verified' ? 'rgba(34,197,94,0.14)' : 'rgba(107,98,91,0.14)', color: it.resolution === 'verified' ? '#15803d' : '#4A433D' }} />
                                                    <Typography sx={{ fontSize: '0.76rem', color: 'text.secondary', minWidth: 120, textAlign: 'right' }}>{formatIstDate(it.resolvedAt)}</Typography>
                                                </Box>
                                            </motion.div>
                                        ))}
                                    </AnimatePresence>
                                )}
                                <TablePaginationBar page={page} total={total} rowsPerPage={25} onPageChange={setPage} />
                            </>
                        )}
                    </Box>
                </Box>
            )}
        </Box>
    );
};

export default ActionablesPage;
